/**
 * Phosphor syntax themes for Shiki (rehype-pretty-code).
 *
 * Single-identity replacements for the GitHub high-contrast pair: both
 * schemes draw from the same restrained four-hue mapping as the
 * `--syntax-*` tokens in globals.css — blue for keywords/types, green for
 * strings/numbers, ochre for callables/attributes, vermilion for tags —
 * over the phosphor card surface. Plain identifiers stay at the base
 * foreground so prose-heavy code reads calm; hue marks structure only.
 *
 * Backgrounds here are informational (keepBackground is off in mdx.tsx);
 * the visible surface is `var(--card)` so scheme flips stay token-driven.
 */

interface ShikiThemeObject {
  name: string;
  type: 'light' | 'dark';
  colors: Record<string, string>;
  /** vscode-textmate raw-theme settings (Shiki's ThemeRegistrationRaw). */
  settings: Array<{
    scope?: string | string[];
    settings: { foreground?: string; background?: string; fontStyle?: string };
  }>;
}

const scopes = (palette: {
  base: string;
  surface: string;
  comment: string;
  string: string;
  number: string;
  keyword: string;
  type: string;
  fn: string;
  attribute: string;
  tag: string;
  operator: string;
  punctuation: string;
}): ShikiThemeObject['settings'] => [
  // Scope-less base entry: unscoped tokens take the identity foreground.
  { settings: { foreground: palette.base, background: palette.surface } },
  {
    scope: ['comment', 'punctuation.definition.comment'],
    settings: { foreground: palette.comment },
  },
  {
    scope: ['string', 'punctuation.definition.string', 'constant.other.symbol'],
    settings: { foreground: palette.string },
  },
  {
    scope: ['constant.numeric', 'constant.character'],
    settings: { foreground: palette.number },
  },
  {
    scope: [
      'keyword',
      'keyword.control',
      'storage.type',
      'storage.modifier',
      'constant.language',
      'variable.language',
    ],
    settings: { foreground: palette.keyword },
  },
  {
    scope: [
      'entity.name.type',
      'entity.name.class',
      'entity.name.namespace',
      'support.type',
      'support.class',
    ],
    settings: { foreground: palette.type },
  },
  {
    scope: ['entity.name.function', 'support.function', 'meta.function-call entity.name'],
    settings: { foreground: palette.fn },
  },
  {
    scope: ['entity.other.attribute-name'],
    settings: { foreground: palette.attribute },
  },
  {
    scope: ['entity.name.tag', 'punctuation.definition.tag'],
    settings: { foreground: palette.tag },
  },
  {
    scope: ['keyword.operator'],
    settings: { foreground: palette.operator },
  },
  {
    scope: ['punctuation', 'meta.brace'],
    settings: { foreground: palette.punctuation },
  },
  // Markdown fences
  {
    scope: ['markup.heading', 'punctuation.definition.heading'],
    settings: { foreground: palette.keyword, fontStyle: 'bold' },
  },
  { scope: ['markup.bold'], settings: { fontStyle: 'bold' } },
  { scope: ['markup.italic'], settings: { fontStyle: 'italic' } },
  { scope: ['markup.inline.raw', 'markup.fenced_code'], settings: { foreground: palette.string } },
];

export const phosphorShikiLight: ShikiThemeObject = {
  name: 'phosphor-light',
  type: 'light',
  colors: {
    'editor.background': '#ffffff',
    'editor.foreground': '#121212',
  },
  settings: scopes({
    base: '#121212',
    surface: '#ffffff',
    comment: '#63636a',
    string: '#0f6b41',
    number: '#0f6b41',
    keyword: '#1e4fc2',
    type: '#1e4fc2',
    fn: '#855610',
    attribute: '#855610',
    tag: '#a91a17',
    operator: '#3d3d3f',
    punctuation: '#3d3d3f',
  }),
};

export const phosphorShikiDark: ShikiThemeObject = {
  name: 'phosphor-dark',
  type: 'dark',
  colors: {
    'editor.background': '#0e0e11',
    'editor.foreground': '#f2f1ef',
  },
  settings: scopes({
    base: '#f2f1ef',
    surface: '#0e0e11',
    comment: '#8e8e95',
    string: '#58d896',
    number: '#58d896',
    keyword: '#6f9dff',
    type: '#6f9dff',
    fn: '#f0b543',
    attribute: '#f0b543',
    tag: '#f0563a',
    operator: '#d9dada',
    punctuation: '#d9dada',
  }),
};
